import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HomeIcon, PaintBucket, Paintbrush, Sun, Shield, Upload, Palette, House, Image } from 'lucide-react';
import backgroundImage from '../images/10246984.jpeg';
import FloorPImg from '../images/FloorPlanIMG.jpg';
import Render1 from '../images/Render1.jpg';
import Render2 from '../images/Render2.jpg';
import Render3 from '../images/Image4.jpg';


const HomeContainer = styled.div`
  background-color: rgba(10, 25, 47, 0);
  color: #ffffff;
`;

const HeroSection = styled.div`
  position: relative;
  isolation: isolate;  /* NEW: keeps z-index layers contained */
  min-height: 90vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  border-bottom: 3px solid #e1ad21;
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
  background-color: #020617;  /* fallback */

  @media (min-width: 1900px) {
    width: 100%;
    height: auto;
  }
`;

const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);

  z-index: 1;            /* was 0 */
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  padding: 20px;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 10;
  max-width: 800px;
  margin-left: auto;
  margin-right: 2rem;
  padding: 2rem;
  text-align: right;

  @media (max-width: 768px) {
    margin-right: 1rem;
    text-align: center;
  }
`;

const HeroTitle = styled.h1`
  font-family: "Raleway-Regular";
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: bold;
  margin-bottom: 1.5rem;
  color: #cec9ab;
`;

const HeroSubtitle = styled.p`
  font-family: "Raleway-Regular";
  color: #cec9ab;
  font-size: clamp(0.9rem, 1.5vw, 1rem);
  margin-bottom: 2rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Button = styled(Link)`
  padding: 0.75rem 1.5rem;
  border-radius: 0.25rem;
  font-family: "Raleway-Regular";
  font-weight: bold;
  text-decoration: none;
  transition: background-color 0.3s;

  &.primary {
    background-color: #cec9ab;
    color: #0a192f;
    &:hover {
      background-color: #cec9ab;
    }
  }

  &.secondary {
    background-color: transparent;
    color: #cec9ab;
    border: 2px solid #cec9ab;
    &:hover {
      background-color: rgba(255, 215, 0, 0.1);
    }
  }
`;

const HowItWorksSection = styled.section`
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: rgb(255, 255, 255);
  padding: 6rem;
  min-height: 10vh;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const HowItWorksContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  margin-top: -5rem;
  margin-bottom: 0rem;
  padding: 0 0rem;
`;

const HowItWorksTitle = styled.h2`
  font-size: clamp(2rem, 3vw, 2.5rem);
  font-family: "Raleway-Regular";
  font-weight: bold;
  margin-bottom: 1rem;
  text-align: center;
  color: #0a192f;
`;

const HowItWorksSubtitle = styled.p`
  font-size: clamp(1rem, 1.5vw, 1.25rem);
  font-family: "Raleway-Regular";
  color: #0a192f;
  margin-bottom: 5rem;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const StepCard = styled(motion.div)`
  background-color: rgba(10, 25, 47, 0.83);
  padding: 2rem;
  border-radius: 0.5rem;
  margin-bottom: 5rem;
`;

const StepIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #cec9ab;
`;

const StepTitle = styled.h3`
  font-size: clamp(1.2rem, 2vw, 1.35rem);
  font-family: "Raleway-Regular";
  font-weight: bold;
  margin-bottom: -1rem;
  color: #cec9ab;
`;

const StepDescription = styled.p`
  font-family: Helvetica;
  font-size: clamp(0.75rem, 1.2vw, 0.8rem);
  margin-bottom: 2rem;
  color: #cec9ab;
  line-height: 1.75;
`;

const StepImage = styled.img`
  width: 100%;
  height: 200px;
  border-radius: 0.5rem;
  pointer-events: none;
  user-select: none;
  
`;

const FeaturesSection = styled.section`
  padding: 6rem;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: rgb(15, 0, 0);
  min-height: 10vh;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const FeatureContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 0rem;
  margin-bottom: 0rem;
  padding: 0 2rem;
`;

const FeatureTitle = styled.h2`
  font-family: "Raleway-Regular";
  font-size: clamp(2rem, 3vw, 2.5rem);
  font-weight: bold;
  margin-bottom: 1rem;
  text-align: center;
  color: #0a192f;
`;

const FeatureSubtitle = styled.p`
  font-family: "Raleway-Regular";
  font-size: clamp(1rem, 1.5vw, 1.25rem);
  margin-bottom: 3rem;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.75;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled(motion.div)`
  background-color: rgba(4, 5, 5, 0.18);
  padding: 2rem;
  border-radius: 0.5rem;
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #0a192f;
`;

const FeatureCardTitle = styled.h3`
  font-family: "Raleway-Regular";
  font-size: clamp(1.25rem, 2vw, 1.59rem);
  font-weight: bold;
  margin-bottom: 1rem;
  color: #0a192f;
`;

const FeatureDescription = styled.p`
  font-family: "Helvetica";
  font-size: clamp(0.9rem, 1.2vw, 1rem);
  line-height: 1.75;
`;
const features = [
  {
    icon: HomeIcon,
    title: "Immersive VR Property Tours",
    description: "Walk through properties in stunning virtual reality with photorealistic detail",
  },
  {
    icon: Paintbrush,
    title: "Customizable Interiors",
    description: "Modify colors, materials, and furniture in real-time to visualize changes",
  },
  {
    icon: Sun,
    title: "Ray-Traced Renderings",
    description: "Generate stunning, photorealistic renders with advanced lighting effects",
  },
  {
    icon: Shield,
    title: "Safety & Flexibility",
    description: "Tour properties remotely and safely while saving time and resources",
  },
];

const steps = [
  {
    icon: Upload,
    title: "Send Floor Plans",
    description: "Send us your floor plans, and we’ll take care of bringing your spaces to life",
    image: FloorPImg,
  },
  {
    icon: Image,
    title: "Create Renders",
    description: "Create photorealistic ray-traced renders to visualize your customized spaces in detail",
    image: Render1,
  },
  {
    icon: House,
    title: "Experience in VR",
    description: "Explore tailored virtual spaces that bring depth and realism to interior design and real estate",
    image: Render2,
    },
  {
    icon: Palette,
    title: "Customize in VR",
    description: "Customize interiors, modify materials, and select furniture easily using our VR interface",
    image: Render3,
  }, 
];


const Home = () => {
  // GoatCounter Script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "//gc.zgo.at/count.js";
    script.async = true;
    script.dataset.goatcounter = "https://nue.goatcounter.com/count";
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Prevent Save and Right-Click
  useEffect(() => {
    const preventSave = (e) => {
      if (e.ctrlKey && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
      }
    };

    const preventRightClick = (e) => {
      e.preventDefault();
    };

    document.addEventListener('keydown', preventSave);
    document.addEventListener('contextmenu', preventRightClick);

    return () => {
      document.removeEventListener('keydown', preventSave);
      document.removeEventListener('contextmenu', preventRightClick);
    };
  }, []);

  // Video Source
  const videoSource = process.env.PUBLIC_URL + '/Fullvid.mp4';

  return (
    <HomeContainer>
      {/* Hero Section */}
      <HeroSection>
        <VideoBackground
  autoPlay
  loop
  muted
  playsInline
  preload="auto"
>
  <source src={videoSource} type="video/mp4" />
  Your browser does not support the video tag.
</VideoBackground>
        <VideoOverlay>
          <img src="/logo512.png" alt="Logo" style={{ width: '150px', height: 'auto' }} />
        </VideoOverlay>
        <HeroContent>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <HeroTitle>Revolutionize Real Estate and Interior Design with Virtual Reality</HeroTitle>
            <HeroSubtitle>Experience properties like never before with immersive VR tours and real-time customization</HeroSubtitle>
            <ButtonContainer>
              <Button to="/contact" className="primary">Get in Touch</Button>
              <Button to="/services" className="secondary">Explore Features</Button>
            </ButtonContainer>
          </motion.div>
        </HeroContent>
      </HeroSection>

      {/* Features Section */}
      <FeaturesSection>
        <FeatureContainer>
          <FeatureTitle>Key Features</FeatureTitle>
          <FeatureSubtitle>Transform your real estate and interior design experience with our cutting-edge VR technology</FeatureSubtitle>
          <FeatureGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <FeatureIcon>
                  <feature.icon />
                </FeatureIcon>
                <FeatureCardTitle>{feature.title}</FeatureCardTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </FeatureContainer>
      </FeaturesSection>

      {/* How It Works Section */}
      <HowItWorksSection>
        <HowItWorksContainer>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <HowItWorksTitle>How It Works</HowItWorksTitle>
            <HowItWorksSubtitle>Transform your properties into immersive virtual experiences in four simple steps</HowItWorksSubtitle>
          </motion.div>
          <StepsGrid>
            {steps.map((step, index) => (
              <StepCard
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <StepIcon>
                  <step.icon />
                </StepIcon>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
                <StepImage src={step.image} alt={step.title} />
              </StepCard>
            ))}
          </StepsGrid>
        </HowItWorksContainer>
      </HowItWorksSection>

    </HomeContainer>
  );
};

export default Home;